// Copy for the Sales portal (/sales; Zooylo staff only). EN is canonical; MS is
// a DRAFT for native review, like the rest of the portal's Malay.
//
// Same rules as dictionary.ts (salesDictionary.test.ts checks them): sentence
// case, no all-caps labels, no stray whitespace, identical keys in EN and MS.
// Placeholders are {name} and are filled with `fill` from dictionary.ts.

import { DEFAULT_LOCALE, type Locale } from "./dictionary";

const en = {
  shell: {
    title: "Zooylo sales",
    subtitle: "Customers, the pipeline, agreements, invoices and the team.",
    eyebrow: "Sales portal",
    nav: "Sales portal sections",
    navAccounts: "Accounts",
    navPipeline: "Pipeline",
    navCodes: "Access codes",
    navBilling: "Billing run",
    navReceivables: "Receivables",
    navReports: "Reports",
    navTeam: "Team",
    demoNote: "Demo data. Nothing here reaches a real organisation. Switch the role to see what each person can do.",
    viewingAs: "Viewing as",
    signedInAs: "Signed in as {email} ({role})",
    readOnly: "Your role can read this but not change it.",
    ownersOnly: "Only an owner can manage the team.",
  },
  roles: {
    owner: "Owner",
    sales: "Sales",
    finance: "Finance",
    support: "Support",
    ownerHint: "Everything, including the team.",
    salesHint: "Accounts, pipeline, access codes, agreements and status.",
    financeHint: "Invoices, the billing run and status.",
    supportHint: "Read only.",
  },
  common: {
    loading: "Loading…",
    errorTitle: "That did not load",
    retry: "Try again",
    save: "Save",
    saving: "Saving…",
    saved: "Saved.",
    cancel: "Cancel",
    edit: "Edit",
    close: "Close",
    none: "—",
    exportCsv: "Export CSV",
    notYet: "Not yet",
    errNotAuth: "Your Zooylo role cannot do this.",
    errInvalid: "Please check the values and try again.",
    errGeneric: "Something went wrong. Please try again.",
    errNotFound: "That record no longer exists.",
    errLastOwner: "The last owner cannot be removed.",
    errNotSignedIn: "That person has not signed in to Zooylo yet. Ask them to sign in once, then add them.",
    errLostReason: "Say why it was lost.",
    errMonthNotEnded: "A month can be invoiced once it has ended.",
    errShareAboveRetail: "Zooylo's share cannot be more than what the family pays.",
    errSstRange: "SST must be between 0 and 100.",
    kindOrg: "Organisation",
    kindIndividual: "Individual practitioner",
    stActive: "Active",
    stSuspended: "Suspended",
    stClosed: "Closed",
  },
  accounts: {
    title: "Accounts",
    search: "Search",
    searchHint: "Name, contact or admin email",
    statusFilter: "Status",
    statusAll: "All",
    count: "{n} of {total} accounts",
    none: "No accounts match.",
    colName: "Account",
    colStatus: "Status",
    colContact: "Contact",
    colLicences: "Licences",
    colPrice: "Zooylo share",
    colOutstanding: "Outstanding",
    licencesLine: "{live} live, {claimed} claimed of {allowance}",
    open: "Open",
    back: "All accounts",
    since: "Customer since {date}",
  },
  details: {
    title: "Details",
    legalName: "Legal name",
    registrationNo: "Registration no.",
    country: "Country (two letters)",
    billingAddress: "Billing address",
    taxId: "Tax id",
    billingEmail: "Billing email",
    contactName: "Contact name",
    contactEmail: "Contact email",
    contactPhone: "Contact phone",
    salesOwner: "Sales owner",
    source: "Source",
    notes: "Notes",
    admins: "Organisation admins",
  },
  agreement: {
    title: "Agreement",
    retail: "Family pays per month",
    share: "Zooylo share per licence",
    keeps: "Organisation keeps",
    keepsHint: "What the family pays, less Zooylo's share.",
    sst: "SST rate (%)",
    currency: "Currency",
    licences: "Family licences",
    terms: "Payment terms",
    terms14: "14 days",
    terms30: "30 days",
    termsOther: "Other",
    termsDays: "Days to pay",
    contractEnd: "Contract end",
    notes: "Agreement notes",
    save: "Save agreement",
    errTerms: "Payment terms must be between 0 and 120 days.",
  },
  onboarding: {
    title: "Onboarding",
    progress: "{done} of {total} steps",
    org_created: "Organisation created",
    admin_signed_in: "Admin signed in",
    first_staff_invited: "First staff member invited",
    first_licence: "First family licence given",
    first_family_signed_in: "First family signed in",
    first_child_shared: "First child shared with the organisation",
    first_invoice: "First invoice issued",
    doneOn: "Done {date}",
  },
  invoices: {
    title: "Invoices",
    issueTitle: "Issue an invoice",
    periodStart: "Period start",
    periodEnd: "Period end",
    amount: "Amount before SST (optional)",
    amountHint: "Leave empty to charge licence-months × Zooylo's share.",
    due: "Due",
    note: "Note on the invoice",
    preview: "Preview: {lm} licence-months, {subtotal} + SST {rate}% {sst} = {total}.",
    issue: "Issue invoice",
    issued: "Invoice issued.",
    none: "No invoices yet.",
    colNumber: "Number",
    colPeriod: "Period",
    colLm: "Licence-months",
    colSubtotal: "Subtotal",
    colSst: "Tax (SST)",
    colTotal: "Total",
    colDue: "Due",
    colStatus: "Status",
    colActions: "Actions",
    markPaid: "Mark paid",
    markVoid: "Void",
    markIssued: "Reopen",
    stDraft: "Draft",
    stIssued: "Issued",
    stPaid: "Paid",
    stVoid: "Void",
  },
  status: {
    title: "Status",
    suspend: "Suspend",
    reactivate: "Reactivate",
    close: "Close account",
    confirm: "Confirm",
    warnSuspend: "Admins can still sign in, but no new family licences can be given until it is reactivated.",
    warnClose: "The organisation is closed. Its admins lose the admin portal.",
    warnReactivate: "The organisation can give family licences again.",
    endLicences: "Also end all family licences now",
    endLicencesWarn: "Every family licence from this organisation ends today. Families lose Premium unless they have their own subscription.",
    saved: "Status changed.",
  },
  audit: {
    title: "Audit",
    none: "No changes recorded yet.",
    system: "Zooylo system",
    created: "{who} created {what}",
    updated: "{who} changed {what}",
    deleted: "{who} removed {what}",
    entity_sales_account: "Account details",
    entity_sales_prospect: "Prospect",
    entity_license: "Agreement",
    entity_org_access_code: "Access code",
    entity_invoice: "Invoice",
    entity_organisation: "Organisation status",
    entity_other: "Record",
    noFieldChanges: "No field changes.",
    more: "{n} more",
  },
  pipeline: {
    title: "Pipeline",
    add: "Add prospect",
    stage_lead: "Lead",
    stage_qualified: "Qualified",
    stage_demo: "Demo",
    stage_proposal: "Proposal",
    stage_negotiating: "Negotiating",
    stage_won: "Won",
    stage_lost: "Lost",
    empty: "None",
    name: "Name",
    kind: "Kind",
    stage: "Stage",
    contactName: "Contact name",
    contactEmail: "Contact email",
    contactPhone: "Contact phone",
    expected: "Expected licences",
    nextAction: "Next action",
    nextActionOn: "Next action date",
    lostReason: "Why it was lost",
    notes: "Notes",
    newTitle: "New prospect",
    editTitle: "Edit {name}",
    errName: "A prospect needs a name.",
    next: "Next: {action}",
    nextOn: "Next: {action}, {date}",
    overdue: "Overdue",
    licencesN: "{n} licences expected",
    createCode: "Create access code",
    linked: "Customer account",
  },
  codes: {
    title: "Access codes",
    newCode: "New access code",
    prefilled: "Filled in from the prospect {name}. Check the terms before creating.",
    label: "Label (who it is for)",
    kind: "Kind",
    seats: "Staff seats",
    allowance: "Family licences",
    retail: "Family pays per month",
    share: "Zooylo share per licence",
    keeps: "Organisation keeps",
    keepsHint: "What the family pays, less Zooylo's share.",
    sst: "SST rate (%)",
    currency: "Currency",
    uses: "Uses",
    expires: "Expires (optional)",
    create: "Create code",
    creating: "Creating…",
    newCodeTitle: "New code",
    newCodeBody: "Shown once. Copy it now and send it to the organisation's admin.",
    copy: "Copy",
    copied: "Copied",
    listTitle: "Codes",
    none: "No codes yet.",
    colCode: "Code",
    colLabel: "Label",
    colKind: "Kind",
    colTerms: "Terms",
    colUses: "Uses",
    colExpires: "Expires",
    termsLine: "{seats} seats, {families} family licences, {price} a month each",
    never: "Never",
  },
  billing: {
    title: "Billing run",
    intro: "Preview every organisation's invoice for one month, then issue them together. Each is due after that organisation's payment terms.",
    month: "Month",
    colOrg: "Organisation",
    colLm: "Licence-months",
    colSubtotal: "Subtotal",
    colSst: "Tax (SST)",
    colTotal: "Total",
    colState: "Invoice",
    alreadyInvoiced: "Already invoiced",
    nothingToBill: "Nothing to bill",
    toIssue: "To issue",
    totals: "Totals",
    none: "No organisation has a price above zero.",
    issueN: "Issue {n} invoices",
    issueOne: "Issue 1 invoice",
    nothing: "Nothing to issue for this month.",
    confirmTitle: "Issue invoices for {month}?",
    confirmBody: "{n} invoices, {total} in all. They cannot be deleted, only voided.",
    issued: "{n} invoices issued.",
  },
  receivables: {
    title: "Receivables",
    intro: "Every unpaid invoice, by how late it is.",
    bucket_current: "Not yet due",
    "bucket_1-30": "1–30 days late",
    "bucket_31-60": "31–60 days late",
    "bucket_61-90": "61–90 days late",
    "bucket_90+": "Over 90 days late",
    total: "Total outstanding",
    groupTotal: "{n} invoices, {total}",
    countN: "{n} invoices",
    none: "Nothing is outstanding.",
    colOrg: "Organisation",
    colNumber: "Invoice",
    colDue: "Due",
    colLate: "Days late",
    colAmount: "Amount",
    markPaid: "Mark paid",
    paid: "Marked paid.",
    emptyBucket: "None",
  },
  reports: {
    title: "Reports",
    mrr: "Monthly recurring revenue",
    mrrHint: "Zooylo's share for every claimed licence at active organisations.",
    orgsActive: "Active organisations",
    orgsSuspended: "{n} suspended",
    licencesLive: "Live family licences",
    licencesClaimed: "{n} claimed by a family",
    receivables: "Receivables",
    paid90: "Paid in the last 90 days",
    lmTitle: "Licence-months by month",
    lmChart: "Licence-months for each of the last six months",
    funnelTitle: "Pipeline funnel",
    exportAccounts: "Export accounts (CSV)",
  },
  team: {
    title: "Team",
    intro: "Who can use the Sales portal, and what each person can change.",
    addTitle: "Add someone",
    addHint: "They must have signed in to Zooylo once with this email.",
    email: "Email",
    role: "Role",
    add: "Add",
    added: "Added.",
    colEmail: "Email",
    colRole: "Role",
    colAdded: "Added",
    remove: "Remove",
    removeTitle: "Remove {email}?",
    removeBody: "They lose the Sales portal straight away. Their past changes stay in the audit.",
    roleFor: "Role for {email}",
    you: "You",
    errEmail: "Enter an email address.",
  },
} as const;

type Translated<T> = { readonly [K in keyof T]: T[K] extends string ? string : Translated<T[K]> };
export type SalesDictionary = Translated<typeof en>;

const ms: SalesDictionary = {
  shell: {
    title: "Jualan Zooylo",
    subtitle: "Pelanggan, saluran jualan, perjanjian, invois dan pasukan.",
    eyebrow: "Portal jualan",
    nav: "Bahagian portal jualan",
    navAccounts: "Akaun",
    navPipeline: "Saluran jualan",
    navCodes: "Kod akses",
    navBilling: "Larian bil",
    navReceivables: "Belum terima",
    navReports: "Laporan",
    navTeam: "Pasukan",
    demoNote: "Data demo. Tiada apa-apa di sini sampai ke organisasi sebenar. Tukar peranan untuk melihat apa yang setiap orang boleh buat.",
    viewingAs: "Dilihat sebagai",
    signedInAs: "Log masuk sebagai {email} ({role})",
    readOnly: "Peranan anda boleh membaca ini tetapi tidak boleh mengubahnya.",
    ownersOnly: "Hanya pemilik boleh mengurus pasukan.",
  },
  roles: {
    owner: "Pemilik",
    sales: "Jualan",
    finance: "Kewangan",
    support: "Sokongan",
    ownerHint: "Semua, termasuk pasukan.",
    salesHint: "Akaun, saluran jualan, kod akses, perjanjian dan status.",
    financeHint: "Invois, larian bil dan status.",
    supportHint: "Baca sahaja.",
  },
  common: {
    loading: "Memuatkan…",
    errorTitle: "Itu tidak dapat dimuatkan",
    retry: "Cuba lagi",
    save: "Simpan",
    saving: "Menyimpan…",
    saved: "Disimpan.",
    cancel: "Batal",
    edit: "Sunting",
    close: "Tutup",
    none: "—",
    exportCsv: "Eksport CSV",
    notYet: "Belum lagi",
    errNotAuth: "Peranan Zooylo anda tidak boleh melakukan ini.",
    errInvalid: "Sila semak nilai dan cuba lagi.",
    errGeneric: "Sesuatu tidak kena. Sila cuba lagi.",
    errNotFound: "Rekod itu sudah tiada.",
    errLastOwner: "Pemilik terakhir tidak boleh dikeluarkan.",
    errNotSignedIn: "Orang itu belum pernah log masuk ke Zooylo. Minta mereka log masuk sekali, kemudian tambah mereka.",
    errLostReason: "Nyatakan sebab ia gagal.",
    errMonthNotEnded: "Sesuatu bulan boleh diinvois selepas ia tamat.",
    errShareAboveRetail: "Bahagian Zooylo tidak boleh melebihi bayaran keluarga.",
    errSstRange: "SST mesti antara 0 dan 100.",
    kindOrg: "Organisasi",
    kindIndividual: "Pengamal individu",
    stActive: "Aktif",
    stSuspended: "Digantung",
    stClosed: "Ditutup",
  },
  accounts: {
    title: "Akaun",
    search: "Cari",
    searchHint: "Nama, kenalan atau e-mel pentadbir",
    statusFilter: "Status",
    statusAll: "Semua",
    count: "{n} daripada {total} akaun",
    none: "Tiada akaun yang sepadan.",
    colName: "Akaun",
    colStatus: "Status",
    colContact: "Kenalan",
    colLicences: "Lesen",
    colPrice: "Bahagian Zooylo",
    colOutstanding: "Tertunggak",
    licencesLine: "{live} aktif, {claimed} dituntut daripada {allowance}",
    open: "Buka",
    back: "Semua akaun",
    since: "Pelanggan sejak {date}",
  },
  details: {
    title: "Butiran",
    legalName: "Nama sah",
    registrationNo: "No. pendaftaran",
    country: "Negara (dua huruf)",
    billingAddress: "Alamat bil",
    taxId: "No. cukai",
    billingEmail: "E-mel bil",
    contactName: "Nama kenalan",
    contactEmail: "E-mel kenalan",
    contactPhone: "Telefon kenalan",
    salesOwner: "Pemilik jualan",
    source: "Sumber",
    notes: "Nota",
    admins: "Pentadbir organisasi",
  },
  agreement: {
    title: "Perjanjian",
    retail: "Keluarga bayar sebulan",
    share: "Bahagian Zooylo setiap lesen",
    keeps: "Organisasi simpan",
    keepsHint: "Bayaran keluarga, tolak bahagian Zooylo.",
    sst: "Kadar SST (%)",
    currency: "Mata wang",
    licences: "Lesen keluarga",
    terms: "Terma bayaran",
    terms14: "14 hari",
    terms30: "30 hari",
    termsOther: "Lain-lain",
    termsDays: "Hari untuk membayar",
    contractEnd: "Tamat kontrak",
    notes: "Nota perjanjian",
    save: "Simpan perjanjian",
    errTerms: "Terma bayaran mesti antara 0 dan 120 hari.",
  },
  onboarding: {
    title: "Permulaan",
    progress: "{done} daripada {total} langkah",
    org_created: "Organisasi dicipta",
    admin_signed_in: "Pentadbir log masuk",
    first_staff_invited: "Kakitangan pertama dijemput",
    first_licence: "Lesen keluarga pertama diberi",
    first_family_signed_in: "Keluarga pertama log masuk",
    first_child_shared: "Anak pertama dikongsi dengan organisasi",
    first_invoice: "Invois pertama dikeluarkan",
    doneOn: "Selesai {date}",
  },
  invoices: {
    title: "Invois",
    issueTitle: "Keluarkan invois",
    periodStart: "Mula tempoh",
    periodEnd: "Akhir tempoh",
    amount: "Jumlah sebelum SST (pilihan)",
    amountHint: "Biarkan kosong untuk mengenakan bulan-lesen × bahagian Zooylo.",
    due: "Tarikh akhir",
    note: "Nota pada invois",
    preview: "Pratonton: {lm} bulan-lesen, {subtotal} + SST {rate}% {sst} = {total}.",
    issue: "Keluarkan invois",
    issued: "Invois dikeluarkan.",
    none: "Belum ada invois.",
    colNumber: "Nombor",
    colPeriod: "Tempoh",
    colLm: "Bulan-lesen",
    colSubtotal: "Subjumlah",
    colSst: "Cukai (SST)",
    colTotal: "Jumlah",
    colDue: "Tarikh akhir",
    colStatus: "Status",
    colActions: "Tindakan",
    markPaid: "Tanda dibayar",
    markVoid: "Batalkan",
    markIssued: "Buka semula",
    stDraft: "Draf",
    stIssued: "Dikeluarkan",
    stPaid: "Dibayar",
    stVoid: "Dibatalkan",
  },
  status: {
    title: "Status",
    suspend: "Gantung",
    reactivate: "Aktifkan semula",
    close: "Tutup akaun",
    confirm: "Sahkan",
    warnSuspend: "Pentadbir masih boleh log masuk, tetapi tiada lesen keluarga baharu boleh diberi sehingga ia diaktifkan semula.",
    warnClose: "Organisasi ditutup. Pentadbirnya kehilangan portal pentadbir.",
    warnReactivate: "Organisasi boleh memberi lesen keluarga semula.",
    endLicences: "Tamatkan juga semua lesen keluarga sekarang",
    endLicencesWarn: "Setiap lesen keluarga daripada organisasi ini tamat hari ini. Keluarga kehilangan Premium melainkan mereka ada langganan sendiri.",
    saved: "Status ditukar.",
  },
  audit: {
    title: "Audit",
    none: "Belum ada perubahan direkodkan.",
    system: "Sistem Zooylo",
    created: "{who} mencipta {what}",
    updated: "{who} mengubah {what}",
    deleted: "{who} membuang {what}",
    entity_sales_account: "Butiran akaun",
    entity_sales_prospect: "Prospek",
    entity_license: "Perjanjian",
    entity_org_access_code: "Kod akses",
    entity_invoice: "Invois",
    entity_organisation: "Status organisasi",
    entity_other: "Rekod",
    noFieldChanges: "Tiada perubahan medan.",
    more: "{n} lagi",
  },
  pipeline: {
    title: "Saluran jualan",
    add: "Tambah prospek",
    stage_lead: "Petunjuk",
    stage_qualified: "Layak",
    stage_demo: "Demo",
    stage_proposal: "Cadangan",
    stage_negotiating: "Rundingan",
    stage_won: "Berjaya",
    stage_lost: "Gagal",
    empty: "Tiada",
    name: "Nama",
    kind: "Jenis",
    stage: "Peringkat",
    contactName: "Nama kenalan",
    contactEmail: "E-mel kenalan",
    contactPhone: "Telefon kenalan",
    expected: "Jangkaan lesen",
    nextAction: "Tindakan seterusnya",
    nextActionOn: "Tarikh tindakan seterusnya",
    lostReason: "Sebab gagal",
    notes: "Nota",
    newTitle: "Prospek baharu",
    editTitle: "Sunting {name}",
    errName: "Prospek perlukan nama.",
    next: "Seterusnya: {action}",
    nextOn: "Seterusnya: {action}, {date}",
    overdue: "Lewat",
    licencesN: "{n} lesen dijangka",
    createCode: "Cipta kod akses",
    linked: "Akaun pelanggan",
  },
  codes: {
    title: "Kod akses",
    newCode: "Kod akses baharu",
    prefilled: "Diisi daripada prospek {name}. Semak terma sebelum mencipta.",
    label: "Label (untuk siapa)",
    kind: "Jenis",
    seats: "Tempat kakitangan",
    allowance: "Lesen keluarga",
    retail: "Keluarga bayar sebulan",
    share: "Bahagian Zooylo setiap lesen",
    keeps: "Organisasi simpan",
    keepsHint: "Bayaran keluarga, tolak bahagian Zooylo.",
    sst: "Kadar SST (%)",
    currency: "Mata wang",
    uses: "Penggunaan",
    expires: "Tamat (pilihan)",
    create: "Cipta kod",
    creating: "Mencipta…",
    newCodeTitle: "Kod baharu",
    newCodeBody: "Ditunjukkan sekali. Salin sekarang dan hantar kepada pentadbir organisasi.",
    copy: "Salin",
    copied: "Disalin",
    listTitle: "Kod",
    none: "Belum ada kod.",
    colCode: "Kod",
    colLabel: "Label",
    colKind: "Jenis",
    colTerms: "Terma",
    colUses: "Penggunaan",
    colExpires: "Tamat",
    termsLine: "{seats} tempat, {families} lesen keluarga, {price} sebulan setiap satu",
    never: "Tidak pernah",
  },
  billing: {
    title: "Larian bil",
    intro: "Pratonton invois setiap organisasi untuk satu bulan, kemudian keluarkan semuanya sekali. Setiap satu perlu dibayar mengikut terma bayaran organisasi itu.",
    month: "Bulan",
    colOrg: "Organisasi",
    colLm: "Bulan-lesen",
    colSubtotal: "Subjumlah",
    colSst: "Cukai (SST)",
    colTotal: "Jumlah",
    colState: "Invois",
    alreadyInvoiced: "Sudah diinvois",
    nothingToBill: "Tiada untuk dibil",
    toIssue: "Akan dikeluarkan",
    totals: "Jumlah keseluruhan",
    none: "Tiada organisasi dengan harga melebihi sifar.",
    issueN: "Keluarkan {n} invois",
    issueOne: "Keluarkan 1 invois",
    nothing: "Tiada apa untuk dikeluarkan bagi bulan ini.",
    confirmTitle: "Keluarkan invois untuk {month}?",
    confirmBody: "{n} invois, {total} kesemuanya. Ia tidak boleh dipadam, hanya dibatalkan.",
    issued: "{n} invois dikeluarkan.",
  },
  receivables: {
    title: "Belum terima",
    intro: "Setiap invois yang belum dibayar, mengikut tahap kelewatan.",
    bucket_current: "Belum sampai tarikh akhir",
    "bucket_1-30": "Lewat 1–30 hari",
    "bucket_31-60": "Lewat 31–60 hari",
    "bucket_61-90": "Lewat 61–90 hari",
    "bucket_90+": "Lewat lebih 90 hari",
    total: "Jumlah tertunggak",
    groupTotal: "{n} invois, {total}",
    countN: "{n} invois",
    none: "Tiada yang tertunggak.",
    colOrg: "Organisasi",
    colNumber: "Invois",
    colDue: "Tarikh akhir",
    colLate: "Hari lewat",
    colAmount: "Jumlah",
    markPaid: "Tanda dibayar",
    paid: "Ditanda dibayar.",
    emptyBucket: "Tiada",
  },
  reports: {
    title: "Laporan",
    mrr: "Hasil berulang bulanan",
    mrrHint: "Bahagian Zooylo bagi setiap lesen yang dituntut di organisasi aktif.",
    orgsActive: "Organisasi aktif",
    orgsSuspended: "{n} digantung",
    licencesLive: "Lesen keluarga aktif",
    licencesClaimed: "{n} dituntut oleh keluarga",
    receivables: "Belum terima",
    paid90: "Dibayar dalam 90 hari lalu",
    lmTitle: "Bulan-lesen mengikut bulan",
    lmChart: "Bulan-lesen bagi setiap enam bulan lalu",
    funnelTitle: "Corong saluran jualan",
    exportAccounts: "Eksport akaun (CSV)",
  },
  team: {
    title: "Pasukan",
    intro: "Siapa boleh menggunakan portal jualan, dan apa yang setiap orang boleh ubah.",
    addTitle: "Tambah seseorang",
    addHint: "Mereka mesti pernah log masuk ke Zooylo sekali dengan e-mel ini.",
    email: "E-mel",
    role: "Peranan",
    add: "Tambah",
    added: "Ditambah.",
    colEmail: "E-mel",
    colRole: "Peranan",
    colAdded: "Ditambah",
    remove: "Keluarkan",
    removeTitle: "Keluarkan {email}?",
    removeBody: "Mereka kehilangan portal jualan serta-merta. Perubahan lalu mereka kekal dalam audit.",
    roleFor: "Peranan untuk {email}",
    you: "Anda",
    errEmail: "Masukkan alamat e-mel.",
  },
};

export const salesDictionaries: Record<Locale, SalesDictionary> = { en, ms };

export function getSalesDictionary(locale: Locale): SalesDictionary {
  return salesDictionaries[locale] ?? salesDictionaries[DEFAULT_LOCALE];
}
